<template>
  <v-autocomplete
    v-if="remote"
    ref="fieldRef"
    v-tooltip:top="getTooltipDisplayValue(remote.valueRef)"
    :menu-props="{ maxWidth: '100%' }"
    :model-value="remote.valueRef"
    :items="remote.itemsRef"
    :search="remote.searchRef"
    :loading="remote.loadingRef"
    :rules="rules"
    :label="props.label"
    v-bind="componentBind"
    @update:search="remote.searchUpdate"
    @blur="onBlur"
    @update:model-value="onUpdateModelValue" />
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { useRemoteAutocompleteSearch } from "@/utils/autocomplete";
import { useFieldTooltip } from "@/composables/useFieldTooltip";

const { getTooltipDisplayValue } = useFieldTooltip();

const emit = defineEmits(["update:modelValue"]);
const props = defineProps({
  remoteSearch: {
    type: Function,
    required: true,
  },
  modelValue: {
    type: [String, null],
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  rules: {
    type: Array,
    required: false,
    default: () => [],
  },
  allowFreeText: {
    type: Boolean,
    required: false,
    default: true,
  },
  componentBind: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});
const fieldRef = ref(null);
const remote = ref(useRemoteAutocompleteSearch(props.remoteSearch, { allowFreeText: props.allowFreeText }));

defineExpose({
  fieldRef,
  remote,
});
// We send in all autocomplete values to the rules
const rules = ref(props.rules.map((rule) => (v) => rule(v, { ...remote.value })));

onMounted(() => {
  remote.value.valueRef = props.modelValue ?? "";
  remote.value.searchUpdate(props.modelValue ?? "");
});

const onBlur = (e) => {
  // If free text is allowed we should update on tab-out
  if (props.allowFreeText) {
    emit("update:modelValue", e.target.value);
  }
};

const onUpdateModelValue = (value) => {
  remote.value.valueRef = value || "";
  emit("update:modelValue", value);
};

watch(
  () => remote.value?.itemsRef,
  () => {
    fieldRef.value?.validate();
  }
);

watch(
  () => props.modelValue,
  () => {
    remote.value.valueRef = props.modelValue;
    remote.value.searchUpdate(props.modelValue);
  }
);
</script>
